/**
 * XSLT stylesheet for prettifying XML with proper indentation and line breaks.
 * Uses standard identity transform with indent="yes" output option.
 */
const xsltPrettifyStylesheet = `<?xml version="1.0" encoding="UTF-8"?>
<xsl:stylesheet version="1.0" xmlns:xsl="http://www.w3.org/1999/XSL/Transform">
  <xsl:output method="xml" encoding="UTF-8" indent="yes" omit-xml-declaration="no"/>
  
  <!-- Identity transform: copy everything -->
  <xsl:template match="@*|node()">
    <xsl:copy>
      <xsl:apply-templates select="@*|node()"/>
    </xsl:copy>
  </xsl:template>
  
</xsl:stylesheet>` as const;

/**
 * Prettifies an XML string with proper indentation and line breaks using XSLT.
 * - If the entire document fails to parse, returns the original XML string.
 * - If there are partial parse errors, returns the formatted result (best effort).
 * - If an uncaught exception occurs, returns the original XML string.
 */
export const prettifyXmlString = (originalXml: string): string => {
  try {
    if (!(window.DOMParser && window.XSLTProcessor)) {
      return originalXml;
    }

    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(originalXml, 'text/xml');

    // Detect parse errors
    // Full parse errors: the document element itself is a parsererror
    // This means the entire document failed to parse
    if (xmlDoc.documentElement.nodeName === 'parsererror') {
      return originalXml;
    }

    // Parse the XSLT stylesheet
    const xsl = parser.parseFromString(xsltPrettifyStylesheet, 'text/xml');

    // Check if XSLT parsing failed
    if (xsl.documentElement.nodeName === 'parsererror') {
      return originalXml;
    }

    // Create an XSLT processor and import the stylesheet
    const xsltProcessor = new XSLTProcessor();
    xsltProcessor.importStylesheet(xsl);

    // Perform the transformation
    const resultDocument = xsltProcessor.transformToDocument(xmlDoc);

    // Serialize the result to string
    const serializer = new XMLSerializer();
    let formatted = serializer.serializeToString(resultDocument);

    // If formatting resulted in empty string
    if (!formatted.trim()) {
      return originalXml;
    }

    // Remove XML declaration if original didn't have one
    const hasDeclaration = originalXml.trim().startsWith('<?xml');
    if (!hasDeclaration && formatted.startsWith('<?xml')) {
      formatted = formatted.replace(/^<\?xml[^?]*\?>\s*/i, '');
    }

    return formatted;
  } catch {
    // Uncaught exception - return original
    return originalXml;
  }
};
